import { useRef } from "react"
import { Link } from "react-router-dom"
import { Container } from "@/components/layout/Container"
import { Eyebrow } from "@/components/shared/Eyebrow"
import { Button } from "@/components/ui/button"
import { LiquidBackground } from "@/components/sections/LiquidBackground"
import { scrollToNextSection } from "@/lib/utils"

interface HeroCta {
	label: string
	to: string
}

interface HeroScrollCta {
	label: string
	/** En lugar de navegar, hace scroll suave a la siguiente sección. */
	scrollToNext: true
}

interface HeroProps {
	/** Texto pequeño sobre el título (opcional). */
	eyebrow?: string
	title: React.ReactNode
	subtitle?: string
	primaryCta?: HeroCta
	secondaryCta?: HeroCta | HeroScrollCta
}

/**
 * Hero reutilizable con fondo de "agua en movimiento" (GSAP + filtro goo) sobre
 * una base clara blanco/azul. El contenido ocupa la mitad izquierda en desktop
 * y se reserva la otra mitad como espacio libre. Una capa clara sobre el líquido
 * garantiza la legibilidad del texto.
 */
export function Hero({ eyebrow, title, subtitle, primaryCta, secondaryCta }: HeroProps) {
	const sectionRef = useRef<HTMLElement>(null)

	return (
		<section
			ref={sectionRef}
			className="relative isolate overflow-hidden bg-linear-to-b from-sky-50 via-background to-background"
		>
			<LiquidBackground />
			{/* Velo claro para asegurar contraste del texto sobre el líquido. */}
			<div
				aria-hidden
				className="pointer-events-none absolute inset-0 bg-linear-to-br from-background/85 via-background/55 to-transparent"
			/>

			<Container className="relative grid items-center gap-10 py-25 md:py-30 lg:grid-cols-2 lg:gap-12 lg:py-38">
				<div className="text-center lg:text-left">
					{eyebrow && (
						<Eyebrow align="center" className="mb-3 lg:text-left">
							{eyebrow}
						</Eyebrow>
					)}
					<h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
						{title}
					</h1>
					{subtitle && (
						<p className="mx-auto mt-6 max-w-xl text-pretty text-lg text-muted-foreground lg:mx-0">{subtitle}</p>
					)}
					{(primaryCta || secondaryCta) && (
						<div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:items-start lg:justify-start">
							{primaryCta && (
								<Button asChild size="lg">
									<Link to={primaryCta.to}>{primaryCta.label}</Link>
								</Button>
							)}
							{secondaryCta &&
								("scrollToNext" in secondaryCta ? (
									<Button size="lg" variant="outline" onClick={() => scrollToNextSection(sectionRef.current)}>
										{secondaryCta.label}
									</Button>
								) : (
									<Button asChild size="lg" variant="outline">
										<Link to={secondaryCta.to}>{secondaryCta.label}</Link>
									</Button>
								))}
						</div>
					)}
				</div>

				{/* Espacio reservado donde antes iba la imagen (solo en desktop). */}
				<div aria-hidden className="hidden lg:block" />
			</Container>
		</section>
	)
}
